import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CodeIcon from '@mui/icons-material/Code';
import axios from 'axios';

const MockInterview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleStartInterview = async () => {
    setLoading(true);
    setError(null);

    try {
      // FIX: Add ?t=timestamp to prevent browser caching
      const response = await axios.get(
        `${process.env.REACT_APP_BACK_END_URL}/mock-interview/random-question?t=${new Date().getTime()}`
      );
      
      const question = response.data;
      
      navigate(`/mock-interview/${question.id}`, { 
        state: { question } 
      });
    } catch (err) {
      console.error('Error starting interview:', err);
      setError('Failed to start interview. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <CodeIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
          AI Mock Interview
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Practice coding interviews with an AI interviewer that provides real-time feedback
        </Typography>
      </Box>

      {/* How it Works */}
      <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          How it Works
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  mx: 'auto',
                  mb: 2
                }}
              >
                1
              </Box>
              <Typography variant="body1" fontWeight="medium">
                A random coding problem will be selected for you
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  mx: 'auto',
                  mb: 2
                }}
              >
                2
              </Box>
              <Typography variant="body1" fontWeight="medium">
                Discuss your approach with AI
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  mx: 'auto',
                  mb: 2
                }}
              >
                3
              </Box>
              <Typography variant="body1" fontWeight="medium">
                Write your solution
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  mx: 'auto',
                  mb: 2
                }}
              >
                4
              </Box>
              <Typography variant="body1" fontWeight="medium">
                Get detailed feedback
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Start Button */}
      <Box sx={{ textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          onClick={handleStartInterview}
          disabled={loading}
          sx={{
            py: 2,
            px: 8,
            fontSize: '1.2rem',
            fontWeight: 'bold',
            borderRadius: 2,
            textTransform: 'none',
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6,
            }
          }}
        >
          {loading ? 'Loading...' : 'Start Interview'}
        </Button>

        {error && (
          <Alert severity="error" sx={{ mt: 3, maxWidth: 500, mx: 'auto' }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Additional Info */}
      <Paper 
        elevation={0} 
        sx={{ 
          mt: 6, 
          p: 3, 
          bgcolor: 'grey.50', 
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2
        }}
      >
        <Typography variant="h6" gutterBottom fontWeight="bold">
          What to Expect
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Questions range from Easy to Hard difficulty
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Code in Python or Java
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Real-time code execution and testing
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Instant feedback on your solution
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Test your code before final submission
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • See detailed error messages and hints
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default MockInterview;